import type { ApolloClient } from '@apollo/client';
import { from, map } from 'rxjs';
import {
    MeasuringPointLmaxAlarmExceedancesDocument,
    type MeasuringPointLmaxAlarmExceedancesQuery,
    type MeasuringPointLmaxAlarmExceedancesQueryVariables,
} from '@/graphql/types';
import { DATA_TYPE } from '../../../../../../graph/enums';
import type { RuntimeDataServiceInput, RuntimeDataServiceOutput } from '../contracts';
import {
    buildSoundAlarmInput,
    buildSoundAlarmInputWithLimit,
    createSoundAlarmExceedanceResultLinks,
    formatSoundAlarmLimitReachedNote,
} from '../soundAlarmExceedances';
import { createTimeRangeColumn } from '../tabular';
import { toSoundMeasuringPointRelayId } from '../measuringPointInput';
import { RuntimeDataService } from '../types';
import type { RuntimeDataServiceRequest, RuntimeTabularColumnDescriptor } from '../types';
import {
    isRecord,
    normalizeArrayEntries,
    normalizeNullableInteger,
    normalizeOptionalPositiveInteger,
    normalizeOrderedTimeWindow,
    normalizeRequiredFiniteNumber,
    normalizeRequiredId,
    normalizeRequiredInteger,
    normalizeString,
} from '../validationPrimitives';

type LmaxAlarmExceedanceEntry =
    RuntimeDataServiceOutput<'measuring_point.lmax_alarm_exceedances'>['entries'][number];
type LmaxAlarmSummaryEntry =
    RuntimeDataServiceOutput<'measuring_point.lmax_alarm_exceedances'>['alarms'][number];

function normalizeLmaxAlarmExceedanceEntry(
    value: unknown,
    pathLabel: string
): LmaxAlarmExceedanceEntry {
    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object.`);
    }

    return {
        startTime: normalizeRequiredFiniteNumber(value.startTime, `${pathLabel}.startTime`),
        endTime: normalizeRequiredFiniteNumber(value.endTime, `${pathLabel}.endTime`),
        name: normalizeString(value.name, `${pathLabel}.name`),
        alarmLevel: normalizeRequiredFiniteNumber(value.alarmLevel, `${pathLabel}.alarmLevel`),
        value: normalizeRequiredFiniteNumber(value.value, `${pathLabel}.value`),
    };
}

function normalizeLmaxAlarmSummaryEntry(value: unknown, pathLabel: string): LmaxAlarmSummaryEntry {
    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object.`);
    }

    return {
        name: normalizeString(value.name, `${pathLabel}.name`),
        alarmLevel: normalizeRequiredFiniteNumber(value.alarmLevel, `${pathLabel}.alarmLevel`),
        counts: normalizeRequiredInteger(value.counts, `${pathLabel}.counts`),
    };
}

function validateLmaxAlarmExceedancesInput(
    input: unknown
): RuntimeDataServiceInput<'measuring_point.lmax_alarm_exceedances'> {
    if (!isRecord(input)) {
        throw new Error('Expected "measuring_point.lmax_alarm_exceedances" input to be an object.');
    }

    const { startTime, endTime } = normalizeOrderedTimeWindow(
        input.startTime,
        input.endTime,
        'measuring_point.lmax_alarm_exceedances'
    );

    const validatedLimit = normalizeOptionalPositiveInteger(
        input.limit,
        'measuring_point.lmax_alarm_exceedances.limit'
    );

    return {
        measuringPointId: normalizeRequiredId(
            input.measuringPointId,
            'measuring_point.lmax_alarm_exceedances.measuringPointId'
        ),
        startTime,
        endTime,
        ...(typeof validatedLimit === 'undefined' ? {} : { limit: validatedLimit }),
    };
}

function validateLmaxAlarmExceedancesResult(
    result: unknown
): RuntimeDataServiceOutput<'measuring_point.lmax_alarm_exceedances'> {
    if (!isRecord(result)) {
        throw new Error(
            'Expected "measuring_point.lmax_alarm_exceedances" result to be an object.'
        );
    }

    const limitReachedValue = normalizeNullableInteger(
        result.limitReachedValue,
        'measuring_point.lmax_alarm_exceedances.limitReachedValue'
    );

    return {
        entries: normalizeArrayEntries(
            result.entries,
            'measuring_point.lmax_alarm_exceedances.entries',
            normalizeLmaxAlarmExceedanceEntry
        ),
        alarms: normalizeArrayEntries(
            result.alarms,
            'measuring_point.lmax_alarm_exceedances.alarms',
            normalizeLmaxAlarmSummaryEntry
        ),
        limitReachedValue,
        limitReachedNote: formatSoundAlarmLimitReachedNote(limitReachedValue),
    };
}

function parseEntriesLinkValue(value: unknown): readonly LmaxAlarmExceedanceEntry[] {
    return normalizeArrayEntries(
        value,
        'measuring_point.lmax_alarm_exceedances.entries',
        normalizeLmaxAlarmExceedanceEntry
    );
}

function parseAlarmsLinkValue(value: unknown): readonly LmaxAlarmSummaryEntry[] {
    return normalizeArrayEntries(
        value,
        'measuring_point.lmax_alarm_exceedances.alarms',
        normalizeLmaxAlarmSummaryEntry
    );
}

function createEntriesColumns() {
    return [
        createTimeRangeColumn<LmaxAlarmExceedanceEntry>(),
        {
            kind: 'text',
            key: 'name',
            label: gettext('ALARM_NAME'),
            defaultAlignment: 'left',
            resolveCell: (entry: LmaxAlarmExceedanceEntry) => entry.name,
        },
        {
            kind: 'measurement',
            key: 'alarm_level',
            label: gettext('ALARM_LEVEL'),
            dataType: DATA_TYPE.LXTMAX,
            defaultAlignment: 'right',
            resolveCell: (entry: LmaxAlarmExceedanceEntry) => entry.alarmLevel,
        },
        {
            kind: 'measurement',
            key: 'value',
            label: gettext('LMAX_ALARM_MEASURED'),
            dataType: DATA_TYPE.LXTMAX,
            defaultAlignment: 'right',
            resolveCell: (entry: LmaxAlarmExceedanceEntry) => entry.value,
        },
    ] as const satisfies readonly RuntimeTabularColumnDescriptor<LmaxAlarmExceedanceEntry>[];
}

function createAlarmSummaryColumns() {
    return [
        {
            kind: 'text',
            key: 'name',
            label: gettext('ALARM_NAME'),
            defaultAlignment: 'left',
            resolveCell: (entry: LmaxAlarmSummaryEntry) => entry.name,
        },
        {
            kind: 'measurement',
            key: 'alarm_level',
            label: gettext('ALARM_LEVEL'),
            dataType: DATA_TYPE.LXTMAX,
            defaultAlignment: 'right',
            resolveCell: (entry: LmaxAlarmSummaryEntry) => entry.alarmLevel,
        },
        {
            kind: 'count',
            key: 'counts',
            label: gettext('EXCEEDANCE_COUNT'),
            defaultAlignment: 'right',
            resolveCell: (entry: LmaxAlarmSummaryEntry) => entry.counts,
        },
    ] as const satisfies readonly RuntimeTabularColumnDescriptor<LmaxAlarmSummaryEntry>[];
}

export class LmaxAlarmExceedancesRuntimeDataService extends RuntimeDataService<'measuring_point.lmax_alarm_exceedances'> {
    static readonly serviceId = 'measuring_point.lmax_alarm_exceedances' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_LMAX_ALARM_EXCEEDANCES_LABEL'),
            description: gettext('DATA_SERVICE_LMAX_ALARM_EXCEEDANCES_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        const entriesColumns = createEntriesColumns();
        const alarmSummaryColumns = createAlarmSummaryColumns();

        return createSoundAlarmExceedanceResultLinks({
            entries: {
                label: gettext('DATA_SERVICE_LMAX_ALARM_EXCEEDANCES_ENTRIES_LABEL'),
                description: gettext('DATA_SERVICE_LMAX_ALARM_EXCEEDANCES_ENTRIES_DESCRIPTION'),
                columns: entriesColumns,
                parseTableRows: parseEntriesLinkValue,
                buildInput: (args) =>
                    buildSoundAlarmInputWithLimit(args, {
                        serviceLabel: 'Lmax alarm exceedances',
                        argPath: 'measuring_point.lmax_alarm_exceedances.entries.arg',
                    }),
            },
            alarms: {
                label: gettext('DATA_SERVICE_LMAX_ALARM_EXCEEDANCES_ALARMS_LABEL'),
                description: gettext('DATA_SERVICE_LMAX_ALARM_EXCEEDANCES_ALARMS_DESCRIPTION'),
                columns: alarmSummaryColumns,
                parseTableRows: parseAlarmsLinkValue,
                buildInput: (args) => buildSoundAlarmInput(args, 'Lmax alarm exceedances'),
            },
            limitReachedNote: {
                label: gettext('DATA_SERVICE_LMAX_ALARM_EXCEEDANCES_LIMIT_REACHED_NOTE_LABEL'),
                description: gettext(
                    'DATA_SERVICE_LMAX_ALARM_EXCEEDANCES_LIMIT_REACHED_NOTE_DESCRIPTION'
                ),
                buildInput: (args) =>
                    buildSoundAlarmInputWithLimit(args, {
                        serviceLabel: 'Lmax alarm exceedances',
                        argPath: 'measuring_point.lmax_alarm_exceedances.limit_reached_note.arg',
                    }),
            },
        });
    }

    constructor(private readonly client: ApolloClient<object>) {
        super();
    }

    validateArgs = validateLmaxAlarmExceedancesInput;

    validateResult = validateLmaxAlarmExceedancesResult;

    createResult$ = ({
        input,
    }: RuntimeDataServiceRequest<'measuring_point.lmax_alarm_exceedances'>) =>
        from(
            this.client.query<
                MeasuringPointLmaxAlarmExceedancesQuery,
                MeasuringPointLmaxAlarmExceedancesQueryVariables
            >({
                query: MeasuringPointLmaxAlarmExceedancesDocument,
                variables: {
                    measuringPointId: toSoundMeasuringPointRelayId(input.measuringPointId),
                    startTime: input.startTime,
                    endTime: input.endTime,
                    ...(typeof input.limit === 'undefined' ? {} : { limit: input.limit }),
                },
                fetchPolicy: 'cache-first',
            })
        ).pipe(map(({ data }) => validateLmaxAlarmExceedancesResult(data.lmaxAlarmExceedances)));
}

export function createLmaxAlarmExceedancesService(
    client: ApolloClient<object>
): LmaxAlarmExceedancesRuntimeDataService {
    return new LmaxAlarmExceedancesRuntimeDataService(client);
}
